import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Inline,
  LoadingState,
  Stack,
  StatePill,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, UnauthenticatedError, apiGet } from "../lib/api.js";
import { useSession } from "../session/SessionProvider.js";
import { usePlatformOwner } from "../access/platform-owner.js";

/**
 * Sprint 41 (GT-TELEGRAM-OPS-BOT-01) — Platform Admin Telegram health
 * page.
 *
 * Surfaces:
 *   - feature-flag posture (`botEnabled`, `sendEnabled`, `webhookEnabled`)
 *   - presence booleans for token + webhook secret (NEVER values)
 *   - linked-chat count + delivery-status summary (counts only)
 *   - closed `nextSteps` checklist + warnings
 *
 * Hard guarantees:
 *   - Platform-owner gated (server-side AND in this UI).
 *   - Page banner: "Readiness only · No real Telegram traffic until
 *     Owner-Go." present regardless of server response.
 *   - The page reads only `/v1/admin/telegram/health` (apiGet).
 *     Never `apiSend` — the page is read-only in Sprint 41.
 *   - Reuses shared design-system primitives.
 */

type TelegramStatus = "not_configured" | "disabled" | "ready_inert" | "active_app_only";

type TelegramNextStep =
  | "configure_bot_token"
  | "configure_webhook_secret"
  | "owner_go_botfather_setup"
  | "owner_go_set_webhook"
  | "owner_go_flip_send"
  | "owner_go_flip_webhook";

type TelegramWarning =
  | "send_enabled_but_token_missing"
  | "webhook_enabled_but_secret_missing"
  | "bot_disabled";

interface TelegramHealthResponse {
  readonly readiness: {
    readonly botEnabled: boolean;
    readonly sendEnabled: boolean;
    readonly webhookEnabled: boolean;
    readonly tokenConfigured: boolean;
    readonly webhookSecretConfigured: boolean;
    readonly deepLinkBaseUrlConfigured: boolean;
    readonly status: TelegramStatus;
    readonly maxCommandsPerMinute: number;
    readonly maxResponseChars: number;
    readonly connectCodeTtlMinutes: number;
  };
  readonly linkedChatsCount: number;
  readonly deliveryStatusSummary: Readonly<Record<string, number>>;
  readonly nextSteps: readonly TelegramNextStep[];
  readonly warnings: readonly TelegramWarning[];
}

const TELEGRAM_STATUSES = new Set<TelegramStatus>([
  "not_configured",
  "disabled",
  "ready_inert",
  "active_app_only",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isTelegramHealthResponse(value: unknown): value is TelegramHealthResponse {
  if (!isRecord(value) || !isRecord(value.readiness)) return false;

  const readiness = value.readiness;
  return (
    TELEGRAM_STATUSES.has(readiness.status as TelegramStatus) &&
    typeof readiness.botEnabled === "boolean" &&
    typeof readiness.sendEnabled === "boolean" &&
    typeof readiness.webhookEnabled === "boolean" &&
    typeof readiness.tokenConfigured === "boolean" &&
    typeof readiness.webhookSecretConfigured === "boolean" &&
    typeof readiness.deepLinkBaseUrlConfigured === "boolean" &&
    typeof readiness.maxCommandsPerMinute === "number" &&
    typeof readiness.connectCodeTtlMinutes === "number" &&
    typeof value.linkedChatsCount === "number" &&
    isRecord(value.deliveryStatusSummary) &&
    Array.isArray(value.nextSteps) &&
    Array.isArray(value.warnings)
  );
}

function isEmptyListPayload(value: unknown): boolean {
  return isRecord(value) && Array.isArray(value.items) && value.items.length === 0;
}

export function TelegramHealthPage() {
  const { t } = useLocale();
  const session = useSession();

  if (!usePlatformOwner(session)) {
    return (
      <Card elevation={1}>
        <EmptyState title={t("admin.telegram.gate.title")} body={t("admin.telegram.gate.body")} />
      </Card>
    );
  }

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <h1
          style={{
            fontSize: "var(--gt-sys-font-size-2xl)",
            fontWeight: "var(--gt-sys-font-weight-bold)",
            margin: 0,
          }}
        >
          {t("admin.telegram.title")}
        </h1>
        <p style={{ color: "var(--gt-sys-text-muted)", margin: 0 }}>
          {t("admin.telegram.subtitle")}
        </p>
        <p
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
            margin: 0,
          }}
          data-testid="admin-telegram-readiness-banner"
        >
          {t("admin.telegram.readiness_banner")}
        </p>
      </Stack>
      <TelegramHealthContent />
    </Stack>
  );
}

/**
 * Shared Telegram-health surface used by gtcc1 and the GTCC mono-deck.
 *
 * Both shells own their platform-owner gate and page heading. Keeping the
 * readiness posture, safe configuration signals, delivery counters and
 * activation checklist in one component prevents the two operator entry
 * points from drifting.
 */
export function TelegramHealthContent({
  queryScope = "admin",
}: {
  readonly queryScope?: "admin" | "gtcc";
} = {}) {
  const { t } = useLocale();
  const query = useQuery<TelegramHealthResponse, Error>({
    queryKey:
      queryScope === "gtcc"
        ? (["gtcc", "tg", "health"] as const)
        : (["admin", "telegram", "health"] as const),
    queryFn: ({ signal }) =>
      apiGet<TelegramHealthResponse>("/v1/admin/telegram/health", undefined, signal),
    staleTime: 30_000,
  });

  if (query.isLoading) return <LoadingState label={t("common.loading")} />;
  if (query.isError) {
    const isUnauth = query.error instanceof UnauthenticatedError;
    const requestId = query.error instanceof ApiError ? query.error.requestId : undefined;
    return (
      <Card elevation={1}>
        <ErrorState
          title={
            isUnauth
              ? t("admin.telegram.error.session_expired_title")
              : t("admin.telegram.error.load_title")
          }
          body={
            isUnauth
              ? t("admin.telegram.error.session_expired_body")
              : requestId
                ? t("admin.telegram.error.load_body_with_request", { requestId })
                : t("admin.telegram.error.load_body")
          }
          action={
            isUnauth ? null : (
              <Button variant="secondary" onClick={() => query.refetch()}>
                {t("admin.telegram.error.retry")}
              </Button>
            )
          }
        />
      </Card>
    );
  }
  if (!isTelegramHealthResponse(query.data)) {
    if (queryScope === "gtcc" && isEmptyListPayload(query.data)) {
      return <EmptyState title={t("admin.telegram.delivery.empty")} />;
    }
    return (
      <Card elevation={1}>
        <ErrorState
          title={t("admin.telegram.error.load_title")}
          body={t("admin.telegram.error.load_body")}
          action={
            <Button variant="secondary" onClick={() => query.refetch()}>
              {t("admin.telegram.error.retry")}
            </Button>
          }
        />
      </Card>
    );
  }
  return (
    <Stack gap={3} data-testid="admin-telegram-content">
      <ReadinessSummaryCard data={query.data} />
      <FlagsCard data={query.data} />
      <DeliverySummaryCard data={query.data} />
      <NextStepsCard data={query.data} />
    </Stack>
  );
}

function statusTone(status: TelegramStatus): StatePillTone {
  switch (status) {
    case "active_app_only":
      return "info";
    case "ready_inert":
      return "warn";
    case "disabled":
      return "neutral";
    case "not_configured":
    default:
      return "neutral";
  }
}

function ReadinessSummaryCard({ data }: { data: TelegramHealthResponse }) {
  const { t } = useLocale();
  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="admin-telegram-readiness-card">
        <Inline gap={2} align="center" justify="space-between" wrap>
          <strong>{t("admin.telegram.readiness.title")}</strong>
          <StatePill
            tone={statusTone(data.readiness.status)}
            label={t(`admin.telegram.status.${data.readiness.status}` as const)}
            dataTestId="admin-telegram-status-pill"
          />
        </Inline>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-sm)",
          }}
        >
          {t(`admin.telegram.status_description.${data.readiness.status}` as const)}
        </span>
        {data.warnings.length > 0 ? (
          <ul
            style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}
            data-testid="admin-telegram-warnings"
          >
            {data.warnings.map((w) => (
              <li
                key={w}
                data-testid={`admin-telegram-warning-${w}`}
                style={{
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--gt-sys-warning, var(--gt-sys-border))",
                  color: "var(--gt-sys-warning, var(--gt-sys-text))",
                  fontSize: "var(--gt-sys-font-size-xs)",
                }}
              >
                {t(`admin.telegram.warning.${w}` as const)}
              </li>
            ))}
          </ul>
        ) : null}
      </Stack>
    </Card>
  );
}

function FlagsCard({ data }: { data: TelegramHealthResponse }) {
  const { t } = useLocale();
  const r = data.readiness;
  const items: Array<{ key: string; label: string; value: string; tone: StatePillTone }> = [
    {
      key: "bot-enabled",
      label: t("admin.telegram.flag.bot_enabled"),
      value: r.botEnabled ? t("common.yes") : t("common.no"),
      tone: r.botEnabled ? "info" : "neutral",
    },
    {
      key: "send-enabled",
      label: t("admin.telegram.flag.send_enabled"),
      value: r.sendEnabled ? t("common.yes") : t("common.no"),
      tone: r.sendEnabled ? "info" : "neutral",
    },
    {
      key: "webhook-enabled",
      label: t("admin.telegram.flag.webhook_enabled"),
      value: r.webhookEnabled ? t("common.yes") : t("common.no"),
      tone: r.webhookEnabled ? "info" : "neutral",
    },
    {
      key: "token-configured",
      label: t("admin.telegram.flag.token_configured"),
      value: r.tokenConfigured ? t("common.yes") : t("common.no"),
      tone: r.tokenConfigured ? "info" : "warn",
    },
    {
      key: "webhook-secret-configured",
      label: t("admin.telegram.flag.webhook_secret_configured"),
      value: r.webhookSecretConfigured ? t("common.yes") : t("common.no"),
      tone: r.webhookSecretConfigured ? "info" : "warn",
    },
    {
      key: "deep-link-base-url-configured",
      label: t("admin.telegram.flag.deep_link_base_url_configured"),
      value: r.deepLinkBaseUrlConfigured ? t("common.yes") : t("common.no"),
      tone: r.deepLinkBaseUrlConfigured ? "info" : "neutral",
    },
    {
      key: "max-commands-per-minute",
      label: t("admin.telegram.flag.max_commands_per_minute"),
      value: String(r.maxCommandsPerMinute),
      tone: "info",
    },
    {
      key: "connect-code-ttl",
      label: t("admin.telegram.flag.connect_code_ttl"),
      value: t("admin.telegram.flag.connect_code_ttl_value", { minutes: r.connectCodeTtlMinutes }),
      tone: "info",
    },
  ];
  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="admin-telegram-flags-card">
        <strong>{t("admin.telegram.flags.title")}</strong>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {t("admin.telegram.flags.subtitle")}
        </span>
        <ul
          style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}
          data-testid="admin-telegram-flags-list"
        >
          {items.map((item) => (
            <li
              key={item.key}
              data-testid={`admin-telegram-flag-row-${item.key}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 12,
                alignItems: "center",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--gt-sys-border)",
                background: "var(--gt-sys-surface)",
              }}
            >
              <strong style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>{item.label}</strong>
              <StatePill
                tone={item.tone}
                label={item.value}
                dataTestId={`admin-telegram-flag-pill-${item.key}`}
              />
            </li>
          ))}
        </ul>
      </Stack>
    </Card>
  );
}

function DeliverySummaryCard({ data }: { data: TelegramHealthResponse }) {
  const { t } = useLocale();
  const totals = Object.entries(data.deliveryStatusSummary);
  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="admin-telegram-delivery-card">
        <strong>{t("admin.telegram.delivery.title")}</strong>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {t("admin.telegram.delivery.subtitle")}
        </span>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "center",
          }}
          data-testid="admin-telegram-linked-chats"
        >
          <strong style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>
            {t("admin.telegram.delivery.linked_chats")}
          </strong>
          <StatePill tone="info" label={String(data.linkedChatsCount)} />
        </div>
        {totals.length === 0 ? (
          <span
            style={{
              color: "var(--gt-sys-text-muted)",
              fontSize: "var(--gt-sys-font-size-xs)",
            }}
            data-testid="admin-telegram-delivery-empty"
          >
            {t("admin.telegram.delivery.empty")}
          </span>
        ) : (
          <ul
            style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}
            data-testid="admin-telegram-delivery-list"
          >
            {totals.map(([status, value]) => (
              <li
                key={status}
                data-testid={`admin-telegram-delivery-row-${status}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 12,
                  alignItems: "center",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--gt-sys-border)",
                }}
              >
                <code style={{ fontSize: "var(--gt-sys-font-size-xs)" }}>{status}</code>
                <StatePill tone="neutral" label={String(value)} />
              </li>
            ))}
          </ul>
        )}
      </Stack>
    </Card>
  );
}

function NextStepsCard({ data }: { data: TelegramHealthResponse }) {
  const { t } = useLocale();
  return (
    <Card elevation={1}>
      <Stack gap={2} data-testid="admin-telegram-next-steps-card">
        <strong>{t("admin.telegram.next_steps.title")}</strong>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {t("admin.telegram.next_steps.subtitle")}
        </span>
        <ol
          style={{ paddingInlineStart: 18, margin: 0, display: "grid", gap: 4 }}
          data-testid="admin-telegram-next-steps-list"
        >
          {data.nextSteps.map((step) => (
            <li
              key={step}
              data-testid={`admin-telegram-next-step-${step}`}
              style={{ fontSize: "var(--gt-sys-font-size-sm)" }}
            >
              {t(`admin.telegram.next_step.${step}` as const)}
            </li>
          ))}
        </ol>
      </Stack>
    </Card>
  );
}
